/* eslint-disable react/no-danger */
import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { getAltTagFromFileName, slugify } from 'utils';
import DOMPurify from 'isomorphic-dompurify';
import { SEO, StaircaseCard, ProductSpotlights, Testimonials } from 'components';
import { Grid } from 'components/ProductPage/Shared';
import { Hero } from 'components/ProductCollectionPage/Shared';

function Stairs({ data }) {
  const { heroData, stairs } = data;
  const { heroImage, heroTitle, heroDescription, gradientPercentage, heroImageOffset, filter } =
    heroData.frontmatter;

  return (
    <>
      <Hero
        heroImage={heroImage}
        gradientPercentage={gradientPercentage}
        heroImageOffset={heroImageOffset}
        filter={filter}
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Stairs"
        breadcrumb="Stairs">
        <h1
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroTitle) }}
          style={{ fontSize: '2.5rem' }}
        />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroDescription) }} />
      </Hero>
      <StyledGrid>
        <InnerGrid className="fade-in">
          {stairs.nodes.map((node) => {
            const { id, frontmatter } = node;
            const { title, slug, shortDescription, cardImage } = frontmatter;
            return (
              <StaircaseCard
                key={id}
                id={id}
                slug={`/stairs/${slugify(slug)}`}
                image={cardImage.childImageSharp.gatsbyImageData}
                title={title}
                description={shortDescription}
                alt={getAltTagFromFileName(cardImage.name)}
              />
            );
          })}
        </InnerGrid>
        <StyledProductSpotlights />
      </StyledGrid>
      <StyledTestimonials />
    </>
  );
}

const StyledGrid = styled(Grid)`
  margin-bottom: -2rem;
`;

const StyledProductSpotlights = styled(ProductSpotlights)`
  padding: 0 1rem;

  @media (width >= 550px) {
    padding: initial;
  }
`;

const InnerGrid = styled.div`
  background-color: #fff;
  border-radius: 0.375rem;
  display: grid;
  gap: 20px;
  grid-column: 1/-1;
  grid-row: auto auto;
  grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  justify-content: center;
  margin: 0 auto;
  max-width: 380px;
  padding: 1.25rem 1rem 2.125rem;
  z-index: 1;

  @media (width >= 450px) {
    margin: -35px auto 0;
    padding: 35px 1.25rem 2.125rem;
  }

  @media (width >= 768px) {
    max-width: initial;
    grid-template-columns: repeat(auto-fit, minmax(324px, 1fr));
  }

  @media (width >= 1000px) {
    grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  }

  @media (width >= 1350px) {
    gap: 30px 20px;
    grid-column: 2/12;
    padding: 35px 30px 2.125rem;
  }

  /* Cards */
  > a {
    grid-column: unset;
    height: 30.375rem;

    @media (width >= 768px) {
      &:last-of-type {
        position: relative;
        left: 50%;
      }
    }

    @media (width >= 1105px) {
      &:last-of-type {
        position: initial;
        left: initial;
        grid-column: 2;
      }
    }
  }
`;

const StyledTestimonials = styled(Testimonials)`
  > h3 {
    padding: 3.125em 0 1.25em;
  }
`;

export const query = graphql`
  query {
    heroData: markdownRemark(fileAbsolutePath: { regex: "/local-data/stairs/index/" }) {
      id
      frontmatter {
        heroTitle
        heroDescription
        heroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
        gradientPercentage
        heroImageOffset
        filter
      }
    }
    stairs: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/local-data/stairs/collections/" } }
      sort: { fileAbsolutePath: ASC }
    ) {
      nodes {
        id
        frontmatter {
          title
          slug
          shortDescription
          cardImage {
            name
            childImageSharp {
              gatsbyImageData(width: 360, height: 240, formats: [AUTO, WEBP, AVIF])
            }
          }
        }
      }
    }
  }
`;

export function Head() {
  return (
    <SEO
      title="Bespoke Stairs Ireland | Connolly Stairs & Doors"
      description="Connolly Stairs & Doors offers bespoke staircases tailored to your needs. From contemporary glass to classic oak, discover your perfect stairs today.">
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Stairs Ireland", "item": "https://www.connollystairs.ie/" },
          { "@type": "ListItem", "position": 2, "name": "Stairs", "item": "https://www.connollystairs.ie/stairs/" }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default Stairs;
